import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ChevronDown } from 'lucide-react';
import { propFirmData } from '../data/propFirmDataHomepage';
import { prefetchBestPropFirmsPage } from '../utils/prefetchBestPropFirmsPage';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading, signInWithGoogle, signOut } = useAuth();
  const { betaActive, betaExpired, betaDaysLeft } = useAccess();

  const userEmail = user?.email ?? '';
  const userAvatarUrl = (() => {
    const meta = user?.user_metadata as any;
    const v = meta?.avatar_url ?? meta?.picture ?? meta?.avatarUrl;
    return typeof v === 'string' && v.trim() ? v : null;
  })();

  const handleSearchSelect = (firmId: string) => {
    setSearchTerm('');
    setShowDropdown(false);
    setMobileOpen(false);
    navigate(`/reviews/${firmId}`);
  };

  const filteredFirms = Object.entries(propFirmData).filter(([_, firm]) =>
    firm.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : 'auto';
  }, [mobileOpen]);

  useEffect(() => {
    if (!showUserDropdown) return;
    const onDocClick = () => setShowUserDropdown(false);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [showUserDropdown]);

  const handleGoogleSignIn = async () => {
    try {
      setAuthBusy(true);
      await signInWithGoogle();
    } catch (error) {
      console.error('Google sign-in failed:', error);
      setAuthBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      setAuthBusy(true);
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setAuthBusy(false);
      setShowUserDropdown(false);
    }
  };

  return (
    <header className="bg-black/80 backdrop-blur sticky top-0 z-50 border-b border-yellow-800 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between w-full">
        <Link 
          to="/" 
          className="flex items-center space-x-3 font-bold text-lg text-yellow-400 no-underline hover:text-yellow-300 transition-colors"
        >
          <img
            src="/assets/logo.svg"
            alt="PropViper"
            className="w-12 h-12 sm:w-10 sm:h-10 object-contain"
          />
          <span className="inline-block tracking-tight text-base sm:text-lg">PropViper</span>
        </Link>

        <div className="hidden md:block relative ml-4 flex-1 max-w-xs">
          <div className="flex items-center bg-white rounded-md overflow-hidden px-2 text-black">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search prop firms..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(e.target.value.length > 0);
              }}
              className="bg-transparent focus:outline-none px-2 py-1 text-sm w-full"
            />
          </div>
          
          {showDropdown && (
            <div className="absolute mt-2 w-full bg-white text-black rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
              {filteredFirms.length > 0 ? (
                filteredFirms.map(([id, firm]) => (
                  <div
                    key={id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => handleSearchSelect(id)}
                  >
                    {firm.name}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-gray-500 text-sm">No results found</div>
              )}
            </div>
          )}
        </div>

        <nav className="hidden md:flex items-center gap-2 text-sm font-semibold ml-4 text-yellow-300">
          <Link
            to="/tools/lot-calculator"
            className="bg-yellow-400 text-black px-3 py-2 rounded-xl hover:bg-yellow-300 transition no-underline"
          >
            Lot Calculator
          </Link>

          <Link
            to="/pricing"
            className="hover:text-yellow-400 transition no-underline px-2 py-2"
          >
            Pricing
          </Link>

          <Link
            to="/best-prop-firms"
            className="hover:text-yellow-400 transition no-underline px-2 py-2"
            onMouseEnter={prefetchBestPropFirmsPage}
          >
            Best Prop Firms
          </Link>
        </nav>

        <div className="flex items-center gap-2 ml-4">
          {betaActive ? (
            <div className="hidden sm:inline-flex items-center rounded-xl border border-yellow-800/60 bg-neutral-950/40 px-2.5 py-1 text-[11px] font-semibold text-yellow-200">
              Beta: {betaDaysLeft ?? "—"} days left
            </div>
          ) : null}

          {betaExpired ? (
            <div className="hidden sm:inline-flex items-center gap-2 rounded-xl border border-yellow-800/60 bg-neutral-950/40 px-2.5 py-1 text-[11px] font-semibold text-yellow-200">
              <span>Beta expired</span>
              <Link to="/pricing" className="underline text-yellow-300 hover:text-yellow-200">
                Upgrade
              </Link>
            </div>
          ) : null}

          {!authLoading && !user ? (
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={authBusy}
              className="hidden sm:inline-flex bg-yellow-400 text-black font-semibold px-3 py-2 rounded-lg hover:bg-yellow-300 transition text-sm disabled:opacity-60"
            >
              Continue with Google
            </button>
          ) : null}

          {!authLoading && !user ? (
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={authBusy}
              className="sm:hidden bg-yellow-400 text-black font-semibold px-3 py-2 rounded-lg hover:bg-yellow-300 transition text-sm disabled:opacity-60"
            >
              Google
            </button>
          ) : null}

          {!authLoading && user ? (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserDropdown((v) => !v);
                }}
                className="flex items-center gap-2 bg-neutral-900/60 hover:bg-neutral-900 border border-yellow-800/60 rounded-lg px-2 py-1.5 transition"
              >
                {userAvatarUrl ? (
                  <img
                    src={userAvatarUrl}
                    alt={userEmail || 'User'}
                    className="w-7 h-7 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-yellow-400 text-black flex items-center justify-center text-xs font-bold">
                    {userEmail ? userEmail.slice(0, 1).toUpperCase() : 'U'}
                  </div>
                )}
                <div className="hidden md:block max-w-[180px] truncate text-xs text-yellow-200">
                  {userEmail}
                </div>
                <ChevronDown className="w-4 h-4 text-yellow-300" />
              </button>

              {showUserDropdown && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-[#1a1a1a] border border-yellow-800 rounded-lg shadow-lg py-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-4 py-2 text-xs text-neutral-300 truncate">
                    Signed in as: {userEmail || user.id}
                  </div>
                  <div className="h-px bg-yellow-800/40 my-1" />
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={authBusy}
                    className="w-full text-left px-4 py-2 text-sm text-yellow-300 hover:bg-yellow-400/10 hover:text-yellow-400 transition disabled:opacity-60"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <button
          className="md:hidden text-yellow-400"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle Menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          id="mobile-menu"
          className="md:hidden bg-black text-yellow-300 px-6 pb-6 pt-4 space-y-6 text-base font-semibold transition-transform z-[100]"
        >
          {betaActive ? (
            <div className="inline-flex items-center rounded-xl border border-yellow-800/60 bg-neutral-950/40 px-3 py-2 text-xs font-semibold text-yellow-200">
              Beta: {betaDaysLeft ?? "—"} days left
            </div>
          ) : null}

          {betaExpired ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-yellow-800/60 bg-neutral-950/40 px-3 py-2 text-xs font-semibold text-yellow-200">
              <span>Beta expired</span>
              <Link to="/pricing" className="underline text-yellow-300 hover:text-yellow-200" onClick={() => setMobileOpen(false)}>
                Upgrade
              </Link>
            </div>
          ) : null}

          {!authLoading && !user ? (
            <button
              type="button"
              onClick={async () => {
                await handleGoogleSignIn();
                setMobileOpen(false);
              }}
              disabled={authBusy}
              className="w-full rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition disabled:opacity-60"
            >
              Continue with Google
            </button>
          ) : null}

          {!authLoading && user ? (
            <div className="rounded-lg border border-yellow-800/60 bg-neutral-950/40 p-4">
              <div className="text-sm text-yellow-200 truncate">{userEmail || user.id}</div>
              <button
                type="button"
                onClick={async () => {
                  await handleLogout();
                  setMobileOpen(false);
                }}
                disabled={authBusy}
                className="mt-3 w-full rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition disabled:opacity-60"
              >
                Logout
              </button>
            </div>
          ) : null}

          <div className="w-full relative">
            <div className="flex items-center bg-white rounded-md overflow-hidden px-2 text-black">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search prop firms..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(e.target.value.length > 0);
                }}
                className="bg-transparent focus:outline-none px-2 py-2 text-sm w-full"
              />
            </div>
            {showDropdown && (
              <div className="absolute mt-2 w-full bg-white text-black rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                {filteredFirms.length > 0 ? (
                  filteredFirms.map(([id, firm]) => (
                    <div
                      key={id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onClick={() => handleSearchSelect(id)}
                    >
                      {firm.name}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-sm">No results found</div>
                )}
              </div>
            )}
          </div>

          <Link 
            to="/compare" 
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
          >
            Compare Firms
          </Link>
          <Link
            to="/best-prop-firms"
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
            onMouseEnter={prefetchBestPropFirmsPage}
          >
            Best Firms
          </Link>
          <Link 
            to="/reviews" 
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
          >
            Read Reviews
          </Link>
          <Link 
            to="/tools/lot-calculator" 
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
            onClick={() => setMobileOpen(false)}
          >
            Lot Calculator
          </Link>
        </div>
      )}
    </header>
  );
}
